import { useState } from 'react';
import { Pencil, Percent, Trash2 } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { formatEuros } from '@jeraisonne/db/cards';
import {
  parsePromoInput,
  promoStatus,
  sortPromotions,
  toLocalInput,
  useDeletePromotion,
  useSavePromotion,
  useSkillcardPromotions,
  type PromoInput,
  type PromoStatus,
  type SkillcardPromotion,
} from '@/lib/admin/offer-admin';

const inputCls =
  'border-ink-200 text-ink-900 focus:ring-primary-400 w-full min-h-[2.75rem] rounded-xl border bg-white px-3 font-sans text-sm focus:outline-none focus:ring-2';

const STATUS: Record<PromoStatus, { label: string; cls: string }> = {
  active: { label: 'En cours', cls: 'bg-success-50 text-success-700' },
  upcoming: { label: 'À venir', cls: 'bg-primary-50 text-primary-700' },
  ended: { label: 'Terminée', cls: 'bg-ink-100 text-ink-600' },
};

function dateLabel(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function emptyInput(): PromoInput {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  const end = new Date(start.getTime() + 7 * 86_400_000);
  return {
    label: '',
    percent: '20',
    starts: toLocalInput(start.toISOString()),
    ends: toLocalInput(end.toISOString()),
  };
}

/**
 * Promos SKILLCARDS (migration 0717) : une remise en % sur TOUS les achats de
 * cartes, packs et programmes (cadeaux et achats dans l'app compris), entre
 * deux dates. Le serveur l'applique au prix (`skillcard_quote`) ; l'app et le
 * site l'annoncent en bandeau. Deux promos en même temps : la plus forte.
 */
export function PromotionsPanel({ unitPriceCents }: { unitPriceCents: number | null }) {
  const promos = useSkillcardPromotions();
  const save = useSavePromotion();
  const del = useDeletePromotion();
  const [input, setInput] = useState<PromoInput>(emptyInput);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const parsed = parsePromoInput(input);
  const example =
    unitPriceCents && parsed.ok
      ? Math.max(50, Math.round((unitPriceCents * (1 - parsed.value.percent / 100)) / 10) * 10)
      : null;

  async function submit() {
    setError(null);
    setDone(null);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    try {
      await save.mutateAsync({ id: editing ?? undefined, ...parsed.value });
      setDone(editing ? 'Promo modifiée.' : 'Promo créée.');
      setEditing(null);
      setInput(emptyInput());
    } catch {
      setError('La promo n’a pas été enregistrée. Réessaie.');
    }
  }

  function edit(p: SkillcardPromotion) {
    setEditing(p.id);
    setDone(null);
    setError(null);
    setInput({
      label: p.label,
      percent: String(p.percent),
      starts: toLocalInput(p.starts_at),
      ends: toLocalInput(p.ends_at),
    });
  }

  function remove(p: SkillcardPromotion) {
    if (!window.confirm(`Supprimer la promo −${p.percent} % ?`)) return;
    del.mutate(p.id, {
      onError: () => setError('La promo n’a pas été supprimée. Réessaie.'),
    });
    if (editing === p.id) {
      setEditing(null);
      setInput(emptyInput());
    }
  }

  const list = sortPromotions(promos.data ?? []);

  return (
    <section
      aria-labelledby="promotions-title"
      className="border-ink-200 space-y-4 rounded-2xl border bg-white p-4 sm:p-5"
    >
      <div>
        <h2
          id="promotions-title"
          className="font-display text-ink-900 flex items-center gap-2 text-lg font-bold"
        >
          <Percent className="h-5 w-5" aria-hidden /> Promos SKILLCARDS
        </h2>
        <p className="text-ink-500 mt-1 text-sm">
          Une réduction sur tous les achats de cartes, packs et programmes, cadeaux compris, entre
          deux dates. Elle s’affiche en bandeau dans l’app et sur le site, avec le prix barré. Deux
          promos en même temps : la plus forte s’applique.
        </p>
      </div>

      <div className="bg-ink-50 ocean:bg-white/5 space-y-3 rounded-xl p-3">
        <p className="text-ink-900 text-sm font-semibold">
          {editing ? 'Modifier la promo' : 'Nouvelle promo'}
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="space-y-1">
            <span className="font-display text-ink-700 block text-xs font-semibold">
              Nom (facultatif)
            </span>
            <input
              className={inputCls}
              placeholder="Vacances de la Toussaint"
              maxLength={60}
              value={input.label}
              onChange={(e) => setInput({ ...input, label: e.target.value })}
            />
          </label>
          <label className="space-y-1">
            <span className="font-display text-ink-700 block text-xs font-semibold">
              Réduction (%)
            </span>
            <input
              className={inputCls}
              inputMode="numeric"
              value={input.percent}
              onChange={(e) => setInput({ ...input, percent: e.target.value })}
            />
          </label>
          <label className="space-y-1">
            <span className="font-display text-ink-700 block text-xs font-semibold">Début</span>
            <input
              type="datetime-local"
              className={inputCls}
              value={input.starts}
              onChange={(e) => setInput({ ...input, starts: e.target.value })}
            />
          </label>
          <label className="space-y-1">
            <span className="font-display text-ink-700 block text-xs font-semibold">Fin</span>
            <input
              type="datetime-local"
              className={inputCls}
              value={input.ends}
              onChange={(e) => setInput({ ...input, ends: e.target.value })}
            />
          </label>
        </div>
        {example != null && unitPriceCents != null && (
          <p className="text-ink-600 text-sm">
            Exemple : une carte à {formatEuros(unitPriceCents)} passe à {formatEuros(example)}.
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          {editing && (
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(null);
                setInput(emptyInput());
              }}
            >
              Annuler
            </Button>
          )}
          <Button onClick={() => void submit()} disabled={save.isPending}>
            {save.isPending ? 'Enregistrement…' : editing ? 'Enregistrer' : 'Créer la promo'}
          </Button>
        </div>
        {error && (
          <p role="alert" className="text-error-600 text-sm">
            {error}
          </p>
        )}
        {done && (
          <p role="status" className="text-success-700 text-sm">
            {done}
          </p>
        )}
      </div>

      {promos.isLoading ? (
        <div className="bg-ink-100 h-16 animate-pulse rounded-xl" />
      ) : list.length === 0 ? (
        <p className="text-ink-500 text-sm">Aucune promo pour l’instant.</p>
      ) : (
        <ul className="space-y-2" aria-label="Promos">
          {list.map((p) => {
            const st = STATUS[promoStatus(p)];
            return (
              <li
                key={p.id}
                className="border-ink-100 flex items-center gap-3 rounded-xl border p-3"
              >
                <span className="font-display text-ink-900 w-14 shrink-0 text-lg font-extrabold">
                  −{p.percent}%
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-ink-900 truncate text-sm font-semibold">
                    {p.label || 'Promo SKILLCARDS'}
                    <span
                      className={cn('ml-2 rounded-full px-2 py-0.5 text-xs font-semibold', st.cls)}
                    >
                      {st.label}
                    </span>
                  </p>
                  <p className="text-ink-500 text-sm">
                    {dateLabel(p.starts_at)} → {dateLabel(p.ends_at)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => edit(p)}
                  aria-label={`Modifier la promo −${p.percent} %`}
                  className="text-ink-600 hover:bg-ink-100 flex h-11 w-11 items-center justify-center rounded-xl"
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => remove(p)}
                  aria-label={`Supprimer la promo −${p.percent} %`}
                  className="text-error-600 hover:bg-error-50 flex h-11 w-11 items-center justify-center rounded-xl"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
